import {
  type ChangeEvent,
  type FormEvent,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PasswordInputWithToggle } from "../components/PasswordInputWithToggle";
import {
  isDuplicateEmailRegistrationError,
  registerPublisher,
  resolveAuthErrorMessage,
  validatePublisherRegistrationInput,
} from "../services/publisherAuth";

type SignInFormValues = {
  fullName: string;
  appName: string;
  email: string;
  password: string;
};

type SignInLocationState = {
  from?: string;
};

const initialValues: SignInFormValues = {
  fullName: "",
  appName: "",
  email: "",
  password: "",
};

export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as SignInLocationState | null) ?? null;
  const [values, setValues] = useState<SignInFormValues>(initialValues);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) {
      return;
    }

    setFormError("");
    setValidationErrors([]);
    const errors = validatePublisherRegistrationInput(values);
    if (errors.length > 0) {
      setValidationErrors([errors[0]]);
      return;
    }

    setSubmitting(true);

    try {
      await registerPublisher(values);
      navigate("/log-in", {
        replace: true,
        state: {
          registered: true,
          email: values.email.trim().toLowerCase(),
          from: locationState?.from,
        },
      });
    } catch (error: unknown) {
      setValidationErrors([]);
      setFormError(resolveAuthErrorMessage(error, "register"));
    } finally {
      setSubmitting(false);
    }
  }

  const errorMessage = formError || validationErrors[0] || "";

  return (
    <article className="oac-screen-04 oac-sign-in-page">
      <section className="oac-sign-in-page__copy">
        <h1 className="oac-sign-in-page__heading">
          <span className="oac-sign-in-page__heading-desktop-only">
            <span>Welcome to the</span>
            <span>OpenAppCart</span>
          </span>
          <span className="oac-sign-in-page__heading-mobile-only">
            <span className="oac-sign-in-page__heading-mobile-line">
              Welcome
            </span>
            <span className="oac-sign-in-page__heading-mobile-line">
              to the
            </span>
            <span className="oac-sign-in-page__heading-mobile-line">
              OpenAppCart
            </span>
          </span>
        </h1>

        <p className="oac-sign-in-page__intro">
          <span className="oac-sign-in-page__intro-desktop-only">
            <span>Whether you&apos;re publishing your first app</span>
            <span>or managing existing apps,</span>
            <span>this is where your journey begins.</span>
          </span>
          <span className="oac-sign-in-page__intro-mobile-only">
            <span className="oac-sign-in-page__intro-mobile-line">
              Whether you&apos;re publishing your
            </span>
            <span className="oac-sign-in-page__intro-mobile-line">
              first app or managing existing apps,
            </span>
            <span className="oac-sign-in-page__intro-mobile-line">
              this is where your journey begins.
            </span>
          </span>
        </p>
      </section>

      <section className="oac-sign-in-page__form-region">
        <form className="oac-sign-in-form" onSubmit={handleSubmit}>
          <div className="oac-sign-in-form__fields">
            <div className="oac-sign-in-form__field">
              <label htmlFor="sign-in-full-name">Full name</label>

              <input
                autoComplete="name"
                id="sign-in-full-name"
                name="fullName"
                onChange={handleChange}
                required
                type="text"
                value={values.fullName}
              />
            </div>

            <div className="oac-sign-in-form__field">
              <label htmlFor="sign-in-app-name">App name</label>

              <input
                autoComplete="organization"
                id="sign-in-app-name"
                name="appName"
                onChange={handleChange}
                required
                type="text"
                value={values.appName}
              />
            </div>

            <div className="oac-sign-in-form__field">
              <label htmlFor="sign-in-email">Email</label>

              <input
                autoComplete="email"
                id="sign-in-email"
                name="email"
                onChange={handleChange}
                required
                type="email"
                value={values.email}
              />
            </div>

            <div className="oac-sign-in-form__field">
              <label htmlFor="sign-in-password">Password</label>

              <PasswordInputWithToggle
                autoComplete="new-password"
                id="sign-in-password"
                maxLength={128}
                name="password"
                onChange={handleChange}
                required
                value={values.password}
              />
            </div>
          </div>

          <div
            aria-live="polite"
            className="oac-sign-in-form__validation"
          >
            {errorMessage ? (
              isDuplicateEmailRegistrationError(errorMessage) ? (
                <p className="oac-form-message oac-form-message--error">
                  An account with this email already exists. Please{" "}
                  <Link
                    className="oac-sign-in-form__validation-link"
                    state={locationState?.from ? { from: locationState.from } : undefined}
                    to="/log-in"
                  >
                    Log In
                  </Link>
                  .
                </p>
              ) : (
                <p className="oac-form-message oac-form-message--error">{errorMessage}</p>
              )
            ) : null}
          </div>

          <div className="oac-sign-in-form__actions">
            <button
              className="oac-sign-in-form__submit"
              disabled={submitting}
              type="submit"
            >
              {submitting ? "Please wait..." : "Sign In"}
            </button>
          </div>
        </form>
      </section>
    </article>
  );
}
